// v2 lizard — a low basking agama: a long flattened body with a pale belly and
// a ridge stripe crowned by tiny crest spikes, a wedge head with a throat
// dewlap, eight two-segment splayed legs with knees out, and a three-segment
// tail tapering away in a live sideways curl. Three liveries: sandy agama,
// blue-grey rock agama with the signature red head, and a banded desert tan.
// Ported 1:1 from age-of-mcp's v2/lizard.ts, using the library's shared
// `wildland` (eyes, tube) helpers.
import * as THREE from "three";
import { defineModel, wildland, type Part } from "../../lib";

const LIVERY = [
  { body: "#ad9550", belly: "#cdbd86", head: "#b8a258", leg: "#8d7a40", ridge: "#8a7438" },
  { body: "#565e66", belly: "#7a8088", head: "#d4582a", leg: "#464d54", ridge: "#3e444b" },
  { body: "#b59560", belly: "#d9c69a", head: "#a98850", leg: "#8f7848", ridge: "#6e5a34" },
];

function lizardParts(v: number): Part[] {
  const c = LIVERY[v % LIVERY.length]!;
  const parts: Part[] = [
    // flattened body, pale belly, dark ridge stripe along the spine
    { geometry: new THREE.IcosahedronGeometry(0.0165, 0), color: c.body,
      position: [0, 0.0125, 0], scale: [2.0, 0.72, 0.95] },
    { geometry: new THREE.IcosahedronGeometry(0.012, 0), color: c.belly,
      position: [0.008, 0.009, 0], scale: [1.6, 0.6, 0.9] },
    { geometry: new THREE.IcosahedronGeometry(0.009, 0), color: c.ridge,
      position: [-0.002, 0.019, 0], scale: [2.2, 0.45, 0.45] },
    // wedge head, snout, throat dewlap
    { geometry: new THREE.IcosahedronGeometry(0.0105, 0), color: c.head,
      position: [0.038, 0.014, 0], scale: [1.5, 0.75, 1.05] },
    { geometry: new THREE.ConeGeometry(0.005, 0.012, 4), color: c.head,
      position: [0.052, 0.0125, 0], rotation: [0, 0, -Math.PI / 2] },
    { geometry: new THREE.IcosahedronGeometry(0.0045, 0), color: c.belly,
      position: [0.044, 0.0075, 0] },
    // tail — three tapering segments curling to one side
    wildland.tube([-0.03, 0.012, 0], [-0.052, 0.011, 0.004], 0.005, 0.0035, c.body),
    wildland.tube([-0.052, 0.011, 0.004], [-0.07, 0.0105, 0.011], 0.0035, 0.0022, c.body),
    wildland.tube([-0.07, 0.0105, 0.011], [-0.084, 0.01, 0.02], 0.0022, 0.0008, c.ridge),
  ];
  // crest spikes along the spine
  for (const sx of [0.012, 0.002, -0.008]) {
    parts.push({ geometry: new THREE.ConeGeometry(0.0022, 0.007, 3),
      color: c.ridge, position: [sx, 0.024, 0] });
  }
  // splayed two-segment legs, knees lifted out
  for (const s of [1, -1] as const) {
    parts.push(
      wildland.tube([0.026, 0.011, s * 0.012], [0.03, 0.013, s * 0.022],
        0.0022, 0.0018, c.leg),
      wildland.tube([0.03, 0.013, s * 0.022], [0.034, 0, s * 0.028],
        0.0016, 0.001, c.leg),
      wildland.tube([-0.018, 0.011, s * 0.013], [-0.022, 0.013, s * 0.024],
        0.0022, 0.0018, c.leg),
      wildland.tube([-0.022, 0.013, s * 0.024], [-0.027, 0, s * 0.03],
        0.0016, 0.001, c.leg),
    );
  }
  parts.push(...wildland.eyes(0.042, 0.019, 0.0085, 0.003, "#16130e"));
  return parts;
}

export const lizardV2 = defineModel({
  id: "lizard-v2",
  batchId: "fable",
  name: "Lizard (v2)",
  tags: ["creature","lizard","low-poly","age-of-mcp","fable"],
  summary: "Low-poly lizard.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.06,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => lizardParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => lizardParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => lizardParts(2) },
  ],
  animations: [],
});
